import { useState, useEffect, useRef } from 'react';
import axiosInstance from '@/utils/axios';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';

export default function MedicationAutocomplete({ value, onChange, placeholder, className, dataTestId }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [allMedications, setAllMedications] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  // Fetch all medications on mount
  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const response = await axiosInstance.get('/medications/suggestions');
        setAllMedications(response.data.medications || []);
      } catch (error) {
        console.error('Erro ao buscar medicamentos:', error);
      }
    };
    fetchMedications();
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setInputValue(val);
    onChange(val);

    // Filter suggestions
    if (val.length >= 2) {
      const filtered = allMedications.filter(med =>
        med.toUpperCase().includes(val)
      ).slice(0, 10); // Limit to 10 suggestions
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (medication) => {
    setInputValue(medication);
    onChange(medication);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        data-testid={dataTestId}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          <Command>
            <CommandList>
              <CommandGroup>
                {suggestions.map((medication, index) => (
                  <CommandItem
                    key={index}
                    onSelect={() => handleSelectSuggestion(medication)}
                    className="cursor-pointer hover:bg-gray-100 px-4 py-2"
                  >
                    {medication}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
